/**
 * Formula clipboard payload schema (versioned for forward compatibility).
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import type {
  FormulaBlocksBody,
  FormulaGroupMode,
  FormulaLayerScope,
  FormulaSlidersBody,
  PerLayerGroupWeight,
} from '@trafficpulse/shared';

export const FORMULA_CLIPBOARD_KIND = 'tp-flow-optimizer';
/** Current payload version. v2 = per-layer group weights; v1 = single number. */
export const FORMULA_CLIPBOARD_VERSION = 2;
/** Versions we can still read (v1 weights are expanded to per-layer on paste). */
export const FORMULA_CLIPBOARD_SUPPORTED_VERSIONS: ReadonlyArray<number> = [1, 2];

export type FormulaClipboardType = 'group' | 'formula';

/** A single formula group, without ids (re-generated on paste). */
export interface FormulaGroupPayload {
  name: string;
  dateRange: string;
  dateRangeCustomFrom?: string;
  dateRangeCustomTo?: string;
  mode: FormulaGroupMode;
  sliders?: FormulaSlidersBody;
  blocks?: FormulaBlocksBody;
  expression: string;
  appliesTo?: FormulaLayerScope;
  /**
   * Bank-row ids this group skips. Copied so a same-flow duplicate keeps its
   * skips, and filtered on paste to the ids the pasted-into flow actually has —
   * a bank id means nothing on another flow, and a stale one would be a skip
   * that silently protects nobody.
   */
  ignoredPathBankIds?: string[];
}

/** Per-group weight entry — v2 per-layer map, or a legacy v1 flat number. */
export type FormulaWeightPayload = PerLayerGroupWeight | number;

/** A whole formula config: groups + the relative weight applied to each. */
export interface FormulaConfigPayload {
  groups: FormulaGroupPayload[];
  /** Same length/order as `groups`; preserves relative weighting (rebalanced on paste). */
  groupWeights: FormulaWeightPayload[];
}

/** Versioned wrapper written to system clipboard and history. */
export interface FormulaClipboardPayload {
  kind: typeof FORMULA_CLIPBOARD_KIND;
  version: number;
  type: FormulaClipboardType;
  /** ISO timestamp the payload was produced. */
  copiedAt: string;
  /** Human-readable label shown in history (group name or "N groups"). */
  label: string;
  data: FormulaGroupPayload | FormulaConfigPayload;
}

/** Stored history entry — same payload + locally-generated id for list keys. */
export interface FormulaClipboardHistoryEntry extends FormulaClipboardPayload {
  id: string;
}
